import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { companyName, sandboxLabel } from "../config/appConfig";
import { useI18n } from "../i18n/I18nContext";
import { DocsNavigationProvider } from "./DocsNavigationContext";
import ServiceIcon from "./ServiceIcon";

const AuthShell = ({ children }) => (
    <main className="auth-shell">
        <section className="auth-panel">{children}</section>
    </main>
);

const LoginGate = ({ children }) => {
    const { configured, error, loading, session, signIn, signOut, reload } = useAuth();
    const { language, setLanguage, t } = useI18n();
    const [isCompact, setIsCompact] = useState(false);
    const [navOpen, setNavOpen] = useState(true);
    const [tokenCopied, setTokenCopied] = useState(false);

    const sessionToken = session?.access_token || "";
    const tokenPreview = sessionToken
        ? `${sessionToken.slice(0, 18)}...${sessionToken.slice(-10)}`
        : t("auth.noToken");

    const copySessionToken = useCallback(async () => {
        if (!sessionToken) return;

        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(sessionToken);
        } else {
            const textarea = document.createElement("textarea");
            textarea.value = sessionToken;
            textarea.setAttribute("readonly", "");
            textarea.style.position = "absolute";
            textarea.style.left = "-9999px";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }

        setTokenCopied(true);
        window.setTimeout(() => setTokenCopied(false), 1800);
    }, [sessionToken]);

    useEffect(() => {
        if (!window.matchMedia) {
            setIsCompact(false);
            setNavOpen(true);
            return undefined;
        }

        const mediaQuery = window.matchMedia("(max-width: 980px)");
        const syncNavigationMode = () => {
            setIsCompact(mediaQuery.matches);
            setNavOpen(!mediaQuery.matches);
        };

        syncNavigationMode();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", syncNavigationMode);
            return () => mediaQuery.removeEventListener("change", syncNavigationMode);
        }

        mediaQuery.addListener(syncNavigationMode);
        return () => mediaQuery.removeListener(syncNavigationMode);
    }, []);

    const navigationValue = useMemo(
        () => ({
            closeNav: () => setNavOpen(false),
            isCompact,
            navOpen,
            sessionControls: {
                copySessionToken,
                sessionToken,
                signOut,
                tokenCopied,
                tokenPreview,
                username: session?.username || "",
            },
            toggleNav: () => setNavOpen((open) => !open),
        }),
        [copySessionToken, isCompact, navOpen, session?.username, sessionToken, signOut, tokenCopied, tokenPreview]
    );

    if (loading) {
        return (
            <AuthShell>
                <div className="auth-brand">
                    <span>{companyName}</span>
                    <small>Sandbox</small>
                </div>
                <div className="auth-status">
                    <span className="auth-spinner" aria-hidden="true" />
                    <div>
                        <p className="docs-eyebrow">{t("auth.cognito")}</p>
                        <h1>{t("auth.validatingSession")}</h1>
                    </div>
                </div>
            </AuthShell>
        );
    }

    if (!configured) {
        return (
            <main className="auth-shell">
                <section className="auth-panel auth-panel-error">
                    <div className="auth-brand">
                        <span>{companyName}</span>
                        <small>Sandbox</small>
                    </div>
                    <div className="auth-copy">
                        <p className="docs-eyebrow">{t("auth.config")}</p>
                        <h1>{t("auth.configMissing")}</h1>
                        <p>{t("auth.configMissingDetail")}</p>
                    </div>
                </section>
            </main>
        );
    }

    if (!session) {
        return (
            <AuthShell>
                <div className="auth-brand">
                    <span>{companyName}</span>
                    <small>Sandbox</small>
                </div>
                <div className="auth-copy">
                    <p className="docs-eyebrow">{sandboxLabel}</p>
                    <h1>{t("auth.privateDocs")}</h1>
                    <p>{t("auth.privateDocsDetail")}</p>
                </div>
                <div className="auth-session-note">
                    <strong>{t("auth.protectedSession")}</strong>
                    <span>{t("auth.protectedSessionDetail")}</span>
                </div>
                {error ? (
                    <div className="auth-error">
                        <strong>{t("auth.loginFailed")}</strong>
                        <span>{error}</span>
                    </div>
                ) : null}
                <div className="auth-actions">
                    <button className="primary-button" type="button" onClick={signIn}>
                        {t("auth.login")}
                    </button>
                    {error ? (
                        <button className="secondary-button" type="button" onClick={reload}>
                            {t("auth.retry")}
                        </button>
                    ) : null}
                </div>
            </AuthShell>
        );
    }

    return (
        <DocsNavigationProvider value={navigationValue}>
            <header className="session-bar">
                <div className="session-brand">
                    <button
                        aria-controls="docs-sidebar"
                        aria-expanded={navOpen}
                        aria-label={navOpen ? t("layout.closeNav") : t("layout.openNav")}
                        className={`session-menu-button${navOpen ? " is-open" : ""}`}
                        type="button"
                        onClick={navigationValue.toggleNav}
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                    <strong>{sandboxLabel}</strong>
                </div>
                <div className="session-spacer" />
                <div className="environment-control">
                    <span>{t("auth.environment")}</span>
                    <strong>Sandbox</strong>
                </div>
                <div className="session-token" title={sessionToken}>
                    <span>{t("auth.tokenActive")}</span>
                    <code>{tokenPreview}</code>
                    <button type="button" onClick={copySessionToken} disabled={!sessionToken}>
                        {tokenCopied ? t("auth.copied") : t("auth.copy")}
                    </button>
                </div>
                <span className="session-user">{session.username}</span>
                <div className="session-provider">
                    <ServiceIcon name="auth" />
                    <strong>{t("auth.auth")}</strong>
                </div>
                <label className="language-select">
                    <span>{t("language.label")}</span>
                    <select value={language} onChange={(event) => setLanguage(event.target.value)}>
                        <option value="es">{t("language.es")}</option>
                        <option value="en">{t("language.en")}</option>
                    </select>
                </label>
                <button type="button" onClick={signOut}>
                    {t("auth.closeSession")}
                </button>
            </header>
            <button
                aria-label={t("layout.closeNav")}
                className={`docs-nav-overlay${isCompact && navOpen ? " is-open" : ""}`}
                type="button"
                onClick={navigationValue.closeNav}
            />
            {children}
        </DocsNavigationProvider>
    );
};

export default LoginGate;
